/**
 * Actycrea: vimeo.esm.js (ES6)
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { toMMSS } from '../helpers/helpers.js';
import { EVENT_PLAYING , EVENT_STOP } from '../helpers/events.js';

const SELECTOR_DATA            = `[data-${PREFIX ?? 'wphack'}-myvm]`;
const SELECTOR_DATA_BUTTON     = `[data-${PREFIX ?? 'wphack'}-button]`;
const SELECTOR_DATA_STATE      = `[data-${PREFIX ?? 'wphack'}-state]`;
const SELECTOR_DATA_TIME       = `[data-${PREFIX ?? 'wphack'}-time]`;
const SELECTOR_DATA_TITLE      = `[data-${PREFIX ?? 'wphack'}-title]`;

const DEFAULTS = {
  id: false,
  videoId: false,
  dimensions: { height: 390, width: 640 },
  fullscreen: false
};

let currentPlayerInstance = null;
let vimeoApiPromise = null;

function loadVimeoAPI( ) {
  if ( window.Vimeo?.Player ) return Promise.resolve( window.Vimeo );
  if ( vimeoApiPromise ) return vimeoApiPromise;

  vimeoApiPromise = new Promise( ( resolve , reject ) => {
    const tag = document.createElement( 'script' );
    tag.src = 'https://player.vimeo.com/api/player.js';
    tag.onload = ( ) => resolve( window.Vimeo );
    tag.onerror = reject;

    const first = document.getElementsByTagName( 'script' )[ 0 ];
    first.parentNode.insertBefore( tag , first );
  });

  return vimeoApiPromise;
}

const PlayerUtils = {
  getCurrent: ( ) => currentPlayerInstance?.player ?? null,
  isPlaying: ( ) => Boolean( currentPlayerInstance?._playing ),
  stopCurrent: ( ) => {
    const p = PlayerUtils.getCurrent( );
    if ( p ) {
      p.pause?.( );
      currentPlayerInstance = null;
    }
  }
};

export class MyVm {
  constructor( element , config = {} ) {
    if ( !( element instanceof HTMLElement ) ) throw new Error( 'MyVm: element inválido' );

    const datasetCfg = {
      id: element.dataset.id ?? false,
      videoId: element.dataset.videoId ?? false,
      fullscreen: element.dataset.fullscreen === 'true' || element.dataset.fullscreen === true || element.dataset.fullscreen === 1 || element.dataset.fullscreen === '1',
      dimensions: {
        width: element.dataset.width ? Number( element.dataset.width ) : undefined,
        height: element.dataset.height ? Number( element.dataset.height ) : undefined
      }
    };

    this._element = element;
    this._config = {
      ...DEFAULTS,
      ...config,
      ...datasetCfg,
      dimensions: {
        ...DEFAULTS.dimensions,
        ...( config.dimensions || {} ),
        ...( datasetCfg.dimensions || {} )
      }
    };

    this._button  = element.querySelector( SELECTOR_DATA_BUTTON );
    this._stateEl = element.querySelector( SELECTOR_DATA_STATE );
    this._timeEl  = element.querySelector( SELECTOR_DATA_TIME );
    this._titleEl = element.querySelector( SELECTOR_DATA_TITLE );

    this._playing = false;
    this._tick = null;
    this._durationSec = 0;
    this.player = null;
    this._ready = null;

    this._onButtonClick = this._onButtonClick.bind( this );
    this._onPlayerReady = this._onPlayerReady.bind( this );
    this._onPlay = this._onPlay.bind( this );
    this._onPause = this._onPause.bind( this );
    this._onEnded = this._onEnded.bind( this );

    this.init( );
  }

  async init( ) {
    if ( !this._config.id || !this._config.videoId ) throw new Error( 'MyVm: requiere "id" e "videoId"' );

    if ( this._button ) this._button.addEventListener( 'click' , this._onButtonClick );

    await loadVimeoAPI( );

    this.player = new window.Vimeo.Player( this._config.id , {
      id: this._config.videoId,
      width: this._config.dimensions.width,
      height: this._config.dimensions.height,
      responsive: true,
      controls: true,
      autoplay: false,
      autopause: true,
      loop: false,
      title: false,
      byline: false,
      portrait: false
    });

    this._ready = this.player.ready( );

    this.player.on( 'loaded' , this._onPlayerReady );
    this.player.on( 'play' , this._onPlay );
    this.player.on( 'pause' , this._onPause );
    this.player.on( 'ended' , this._onEnded );
  }

  async _onPlayerReady( ) {
    const iframe = this._element?.querySelector( 'iframe' );

    if ( iframe ) {
      iframe.setAttribute( 'itemprop' , 'embedUrl' );
    }

    await this._updateTitle( );
    await this._refreshDuration( );
    this._setState( 'play' );
    this._renderTime( 0 , this._durationSec ?? 0 );
  }

  _onPlay( ) {
    this._playing = true;
    this._element.classList.add( 'playing' );
    this._setState( 'pause' );
    this._startTicker( );
    this._element.dispatchEvent( EVENT_PLAYING );
  }

  _onPause( ) {
    this._playing = false;
    this._element.classList.remove( 'playing' );
    this._setState( 'play' );
    this._stopTicker( );
    this._element.dispatchEvent( EVENT_STOP );
  }

  _onEnded( ) {
    this._playing = false;
    this._element.classList.remove( 'playing' );
    this._setState( 'play' );
    this._stopTicker( );
    this._renderTime( 0 , this._durationSec ?? 0 );
    this._element.dispatchEvent( EVENT_STOP );

    if ( currentPlayerInstance === this ) currentPlayerInstance = null;
  }

  async _onButtonClick( e ) {
    e.preventDefault( );

    if ( !this.player ) return;

    try {
      await this._ready;

      if ( currentPlayerInstance && currentPlayerInstance !== this ) PlayerUtils.stopCurrent( );

      if ( !this._playing ) {
        currentPlayerInstance = this;
        await this.player.play( );
        if ( this._button ) this._button.setAttribute( 'aria-pressed' , 'true' );
      } else {
        currentPlayerInstance = null;
        await this.player.pause( );
        if ( this._button ) this._button.setAttribute( 'aria-pressed' , 'false' );
      }
    } catch ( error ) {
      console.error( 'MyVm: error al reproducir Vimeo' , error );
    }
  }

  async play( ) {
    if ( currentPlayerInstance && currentPlayerInstance !== this ) PlayerUtils.stopCurrent( );
    currentPlayerInstance = this;
    await this._ready;
    await this.player?.play?.( );
  }

  async pause( ) {
    await this.player?.pause?.( );
    if ( currentPlayerInstance === this ) currentPlayerInstance = null;
  }

  async stop( ) {
    await this.player?.pause?.( );
    await this.player?.setCurrentTime?.( 0 );
    if ( currentPlayerInstance === this ) currentPlayerInstance = null;
  }

  destroy( ) {
    try {
      this._stopTicker( );
      this.pause( );
      this.player?.destroy?.( );
    } catch ( _ ) { /* ignore */ }

    if ( this._button ) this._button.removeEventListener( 'click' , this._onButtonClick );

    this.player = null;
    this._element = null;
  }

  async _updateTitle( ) {
    const dsTitle = this._titleEl?.dataset?.[ `${PREFIX}Title` ] || this._titleEl?.dataset?.wphackTitle;
    let apiTitle = '';

    try {
      apiTitle = await this.player?.getVideoTitle?.( );
    } catch ( _ ) {
      apiTitle = '';
    }

    const title = dsTitle || apiTitle || '';
    if ( this._titleEl && title ) this._titleEl.textContent = title;
  }

  async _refreshDuration( ) {
    try {
      const dur = await this.player?.getDuration?.( );
      this._durationSec = Math.floor( Number( dur || 0 ) );
    } catch ( _ ) {
      this._durationSec = 0;
    }

    this._renderTime( 0 , this._durationSec );
  }

  _setState( kind ) {
    if ( !this._stateEl ) return;

    const map = {
      play:  this._stateEl.dataset.play  || 'PLAY',
      pause: this._stateEl.dataset.pause || 'PAUSE',
      stop:  this._stateEl.dataset.stop  || 'STOP'
    };

    const label = map[ kind ] || '';
    if ( label ) this._stateEl.textContent = label;
  }

  _renderTime( currentSec , totalSec ) {
    if ( !this._timeEl ) return;

    const forceHours = Number( totalSec ) >= 3600;
    const cur = toMMSS( currentSec , { forceHours } );
    const tot = toMMSS( totalSec , { forceHours } );

    this._timeEl.textContent = `${cur}/${tot}`;
  }

  _startTicker( ) {
    this._stopTicker( );

    this._tick = setInterval( async ( ) => {
      let cur = 0;
      let tot = this._durationSec ?? 0;

      try {
        cur = Number( await this.player?.getCurrentTime?.( ) || 0 );
        tot = Number( this._durationSec || await this.player?.getDuration?.( ) || 0 );
      } catch ( _ ) {
        cur = 0;
      }

      this._renderTime( cur , tot );
    } , 250 );
  }

  _stopTicker( ) {
    if ( this._tick ) {
      clearInterval( this._tick );
      this._tick = null;
    }
  }

  static initAll( root = document ) {
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];

    nodes.forEach( ( el ) => {
      if ( !el._myvmInstance ) el._myvmInstance = new MyVm( el );
      instances.push( el._myvmInstance );
    });

    return instances;
  }
}

export const UtilsVm = PlayerUtils;
export async function ensureVimeoAPI( ) { return loadVimeoAPI( ); }
export default MyVm;